import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { CTA, CustomSelect, Icon, Text } from '../../../atoms';
import { CustomBox, InnerBox, Separator, Warning } from '../style';
import ButtonsBanner from '../ButtonsBanner';
import { MODAL_OPEN } from '../../../../app/redux/actions';
import InputLegend from '../../../atoms/InputLegend';
import { InputWrapper } from '../../../atoms/CustomInput/style';
import { GroupCard, InputsWrapper, Legend, RegistrationWrapper } from './style';
import ModalGroups, { ModalGroupsId } from '../../../atoms/Modals/ModalGroups';
import {
  COURSE_TAB_SAVE,
  GROUP_LIST_GET,
  REMOVE_NEW_GROUP,
  TUTOR_DISABLED_TAB_SET,
} from '../../../../app/redux/actions/tutors';
import { timestampToDate } from '../../../../utils/common';
import { FFOO_GET } from '../../../../app/redux/actions/courselist';
import ModalCreateCourse, {
  ModalCreateCourseId,
} from '../../../atoms/Modals/ModalCreateCourse';
import CustomTooltip from '../../../atoms/CustomTooltip';
import { formatSelectOpt } from '../../../../utils/labels';
import { withMediaQueries } from '../../../../app/hoc/withMediaQueries';
import ModalCreateEvent, {
  ModalCreateEventId,
} from '../../../atoms/Modals/ModalCreateEvent';

const Registration = ({
  isEvent,
  mediaIsPhone,
  groups,
  ffoo,
  getFfoo,
  newGroupIds,
  course,
  activeTab,
  getGroups,
  modalOpen,
  removeNewGroup,
  courseTabSave,
  setDisabledTab,
  isSupervisor,
}) => {
  const { editMode, registration } = course;
  const [selectedGroupId, setSelectedGroupId] = useState();
  const [addedGroupIds, setAddedGroupIds] = useState([]);
  const [ffooIds, setFfooIds] = useState([]);

  const isValidTab = useCallback(() => {
    if (isSupervisor) return true;
    const tutorGroupsAddedIds = groups
      ?.map((g) => g?.id?.toString())
      ?.filter(
        (gId) => newGroupIds?.includes(gId) || addedGroupIds?.includes(gId)
      );
    return tutorGroupsAddedIds?.length > 0 || newGroupIds?.length > 0;
  }, [groups, addedGroupIds, isSupervisor, newGroupIds]);

  useEffect(() => {
    if (editMode && registration) {
      const edidFffooIds = (registration?.mandatory_ffoo_ids || []).map((id) =>
        id?.toString()
      );
      const edidGroupIds = (registration?.included_groups || []).map((id) =>
        id?.toString()
      );
      setFfooIds(edidFffooIds);
      setAddedGroupIds(edidGroupIds);
    } else {
      setFfooIds([]);
      setAddedGroupIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode]);

  useEffect(() => {
    getGroups();
    if (ffoo?.length <= 0) getFfoo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (editMode || activeTab === 4) {
      setDisabledTab({ 4: { completed: isValidTab() } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, activeTab, addedGroupIds, newGroupIds, groups]);

  const onAddGroup = () => {
    const newGroup = groups?.find((g) => g?.id === selectedGroupId);
    setSelectedGroupId();
    setAddedGroupIds([...addedGroupIds, newGroup?.id?.toString()]);
  };

  const onRemoveGroup = (id) => {
    setAddedGroupIds(
      addedGroupIds.filter((agId) => agId?.toString() !== id?.toString())
    );
  };

  return (
    <>
      <RegistrationWrapper>
        <Text type="b3" mb={24}>
          Sezione dedicata alle iscrizioni degli utenti al percorso formativo
          che si sta creando
        </Text>
        <CustomBox>
          <Text type="h2" mb={24}>
            Iscrizione utenti discenti
          </Text>
          <InputLegend label="Obbligatorietà" htmlFor="course-mandatory" />
          <InputWrapper>
            <CustomSelect
              size={mediaIsPhone ? 'full' : 'm'}
              id="course-mandatory"
              options={(ffoo || []).map((fo) => formatSelectOpt(fo))}
              onChange={(v) =>
                setFfooIds(v?.map((fo) => fo?.value?.toString()))
              }
              selected={(ffoo || [])
                .filter((fo) => ffooIds?.includes(fo.id?.toString()))
                .map((fo) => formatSelectOpt(fo))}
              isMulti
            />
          </InputWrapper>
          <Separator />
          <Text type="b3" mb={32}>
            Seleziona uno o più gruppi di discenti che vuoi iscrivere al
            percorso formativo oppure crea un nuovo gruppo
          </Text>

          <InputLegend
            label="Seleziona gruppo"
            htmlFor="course-groups"
            required={!isSupervisor}
          />
          <InputsWrapper>
            <CustomSelect
              size={mediaIsPhone ? 'full' : 'l'}
              id="course-groups"
              options={(groups || [])
                .filter(
                  (g) =>
                    addedGroupIds.findIndex(
                      (agId) => agId === g.id?.toString()
                    ) < 0 &&
                    !newGroupIds?.includes(g.id) &&
                    g?.id
                )
                .map((g) => ({
                  value: g?.id,
                  label: g?.name,
                }))}
              onChange={({ value }) => setSelectedGroupId(value)}
              selected={(groups || [])
                .filter((g) => g.id === selectedGroupId)
                .map((g) => ({
                  value: g?.id,
                  label: g?.name,
                }))}
              zIndexMenu={12}
            />
            <CTA
              btnText="Aggiungi"
              disabled={!selectedGroupId}
              onClick={onAddGroup}
              fullsize={mediaIsPhone}
            />
            <div className="d-flex">
              <Text type="b3">oppure</Text>
              {mediaIsPhone && (
                <CustomTooltip
                  type="secondary"
                  title="Gestisci i gruppi"
                  tooltipKey="course_groups"
                />
              )}
            </div>
            <CTA
              type="secondary"
              btnText="CREA NUOVO GRUPPO"
              onClick={() => modalOpen(ModalGroupsId)}
              fullsize={mediaIsPhone}
            />
            {!mediaIsPhone && (
              <CustomTooltip
                type="secondary"
                title="Gestisci i gruppi"
                tooltipKey="course_groups"
              />
            )}
          </InputsWrapper>

          {(addedGroupIds?.length > 0 || newGroupIds?.length > 0) && (
            <InnerBox>
              <Text type="h2" mb={32}>
                Gruppi iscritti
              </Text>
              <Text type="b4" mb={24}>
                {(addedGroupIds?.length || 0) + (newGroupIds?.length || 0)}{' '}
                Gruppi aggiunti
              </Text>
              {!mediaIsPhone && (
                <Legend>
                  <Text type="h3.5">nome gruppo</Text>
                  <Text type="h3.5">numero utenti</Text>
                  <Text type="h3.5">DATA DI CREAZIONE</Text>
                </Legend>
              )}
              {(groups || [])
                .filter(
                  (g) =>
                    newGroupIds?.includes(g?.id?.toString()) ||
                    addedGroupIds?.includes(g?.id?.toString())
                )
                .map((g) => {
                  if (mediaIsPhone) {
                    return (
                      <GroupCard key={g.id}>
                        <Text type="h5">nome gruppo</Text>
                        <Text type="h3">{g.name}</Text>
                        <Text type="h5">numero utenti</Text>
                        <Text type="b4">{g.total_users} utenti</Text>
                        <Text type="h5">DATA DI CREAZIONE</Text>
                        <Text type="b4">
                          {timestampToDate(g.creation_date)}
                        </Text>
                        {g?.owned && (
                          <>
                            <div />
                            <CTA
                              type="tertiary"
                              btnText="Modifica"
                              icon="edit-05"
                              onClick={() => modalOpen(ModalGroupsId, g)}
                              bottomRight
                            />
                          </>
                        )}
                        <div />
                        <CTA
                          type="tertiary"
                          btnText="RIMUOVI gruppo"
                          icon="users-x"
                          onClick={() => {
                            onRemoveGroup(g.id);
                            removeNewGroup(g.id);
                          }}
                          bottomRight
                        />
                      </GroupCard>
                    );
                  }
                  return (
                    <GroupCard key={g.id}>
                      <Text type="h3">{g.name}</Text>
                      <Text type="b4">{g.total_users} utenti</Text>
                      <Text type="b4">{timestampToDate(g.creation_date)}</Text>
                      <div className="d-flex g-48 btns-wrapper">
                        {g?.owned && (
                          <CTA
                            type="tertiary"
                            btnText="Modifica"
                            icon="edit-05"
                            onClick={() => modalOpen(ModalGroupsId, g)}
                          />
                        )}
                        <CTA
                          type="tertiary"
                          btnText="RIMUOVI gruppo"
                          icon="users-x"
                          onClick={() => {
                            onRemoveGroup(g.id);
                            removeNewGroup(g.id);
                          }}
                        />
                      </div>
                    </GroupCard>
                  );
                })}
            </InnerBox>
          )}
        </CustomBox>
      </RegistrationWrapper>
      {mediaIsPhone && (
        <Warning className="mt-5">
          <Icon type="alert-circle" size={16} />
          <Text type="b4" className="warning d-flex align-start">
            Se non selezioni/crei un gruppo, il percorso formativo sarà visibile
            a tutti i gruppi a te assegnati
          </Text>
        </Warning>
      )}
      <ButtonsBanner
        warning="Se non selezioni/crei un gruppo, il percorso formativo sarà visibile a tutti i gruppi a te assegnati"
        onClickConfirm={() => {
          courseTabSave({
            tabName: 'registration',
            data: {
              included_groups: [
                ...addedGroupIds.map((gId) => gId),
                ...newGroupIds,
              ],
              mandatory_ffoo_ids: ffooIds,
            },
          });
          modalOpen(isEvent ? ModalCreateEventId : ModalCreateCourseId);
        }}
      />
      <ModalGroups />
      {isEvent ? <ModalCreateEvent /> : <ModalCreateCourse />}
    </>
  );
};

Registration.propTypes = {
  isEvent: PropTypes.bool,
  mediaIsPhone: PropTypes.bool,
  // HOC: state
  groups: PropTypes.array,
  newGroupIds: PropTypes.array,
  ffoo: PropTypes.array,
  course: PropTypes.object,
  activeTab: PropTypes.number,
  isSupervisor: PropTypes.bool,
  // HOC: dispatch
  getGroups: PropTypes.func,
  getFfoo: PropTypes.func,
  modalOpen: PropTypes.func,
  removeNewGroup: PropTypes.func,
  courseTabSave: PropTypes.func,
  setDisabledTab: PropTypes.func,
};

export default connect(
  (state) => {
    const {
      groups: { list: groups },
      newGroupIds,
      course,
      activeTab,
    } = state.tutors;
    const { ffoo } = state.courselist;
    const { isSupervisor } = state.user;
    return { groups, newGroupIds, ffoo, course, activeTab, isSupervisor };
  },
  (dispatch) => ({
    modalOpen: (id, payload = {}) =>
      dispatch({ type: MODAL_OPEN, id, payload }),
    getGroups: () => dispatch({ type: GROUP_LIST_GET._REQUEST }),
    removeNewGroup: (id) => dispatch({ type: REMOVE_NEW_GROUP, id }),
    courseTabSave: ({ tabName, data }) =>
      dispatch({ type: COURSE_TAB_SAVE, tabName, data }),
    getFfoo: () => dispatch({ type: FFOO_GET._REQUEST }),
    setDisabledTab: (tabs) => dispatch({ type: TUTOR_DISABLED_TAB_SET, tabs }),
  })
)(withMediaQueries(Registration));
